// src/lib/menage-exception.js
//
// « COMMENT JE VOIS QU'UN MÉNAGE PORTE UNE EXCEPTION ? » (Dimitri, 2026-08-06)
//
// Une règle envoie tous les ménages futurs d'un logement chez un agent. On peut
// déroger sur un ménage précis — « sauf le 10 août, c'est Néïsia » — et cette
// dérogation ne casse pas la règle : la règle n'est lue qu'à la CRÉATION du
// ménage, jamais relue ensuite.
//
// Rien à l'écran ne distinguait ces deux cas. Un ménage assigné à Néïsia par
// dérogation et un ménage assigné à Néïsia parce que c'est la règle avaient
// rigoureusement la même apparence.
//
// ⚠️ CE QUI REND LE REPÈRE UTILE : il nomme l'agent que la RÈGLE aurait choisi.
// Un badge qui dirait seulement « exception » signale qu'on déroge sans dire à
// QUOI — il faudrait aller ouvrir la fiche du logement pour le savoir, ce qui
// annule l'intérêt du repère.
//
// Helpers PURS : aucun accès réseau, aucun DOM.

/** Email exploitable en minuscules, ou null. Un email n'est pas sensible à la casse. */
function normEmail(v) {
  if (typeof v !== 'string') return null
  const t = v.trim().toLowerCase()
  return t === '' ? null : t
}

/**
 * Ce ménage déroge-t-il à la règle de son logement ?
 *
 * NON dans ces trois cas, et chacun pour une raison distincte :
 *   - aucune règle → on ne peut pas déroger à rien ;
 *   - ménage NON ASSIGNÉ → c'est un orphelin, déjà signalé par son propre
 *     repère (liseré terracotta). Le marquer aussi « exception » ferait deux
 *     alertes pour un seul fait, et diluerait les deux ;
 *   - agent identique à la règle → c'est le cas nominal.
 *
 * @param {object} run   — { assigned_provider_email }
 * @param {object} regle — { provider_email } ou null
 */
export function estException(run, regle) {
  const assigne = normEmail(run?.assigned_provider_email)
  const attendu = normEmail(regle?.provider_email)
  if (!attendu) return false
  if (!assigne) return false
  return assigne !== attendu
}

/**
 * Libellé du repère. Renvoie null quand il n'y a rien à signaler — l'appelant
 * n'a donc pas à re-tester.
 *
 * @param {Function} [nomDe] — email → nom lisible. À défaut, l'email est affiché
 *   tel quel : mieux vaut un email brut qu'un badge muet.
 */
export function libelleException(run, regle, nomDe) {
  if (!estException(run, regle)) return null
  const attendu = normEmail(regle?.provider_email)
  const nom = typeof nomDe === 'function' ? nomDe(attendu) : null
  return `exception · règle : ${nom || attendu}`
}

/**
 * Peut-on proposer « Revenir à la règle » sur ce ménage ?
 *
 * Même condition que l'exception, plus une : le ménage ne doit pas être TERMINÉ.
 * Réassigner un ménage fait changerait le destinataire d'un travail déjà
 * exécuté — le tarif est figé à la création et il est dû au prestataire qui l'a
 * fait (doctrine gel comptable, ADR-223). Un ménage terminé garde donc son
 * badge, mais sans bouton.
 */
export function peutRevenirALaRegle(run, regle) {
  if (!estException(run, regle)) return false
  return run?.status !== 'completed'
}

/**
 * Que proposer pour revenir à la règle ?
 *
 * ⚠️ L'agent de la règle n'est pas forcément assignable PAR CELUI QUI REGARDE.
 * L'admin peut poser une règle vers un agent qui n'est pas dans le pool de
 * l'orchestrateur : le verrou refuserait alors sa réassignation. Proposer un
 * bouton qui échoue est pire que ne rien proposer — l'orchestrateur croirait
 * avoir corrigé, et le ménage partirait quand même chez le mauvais agent.
 *
 * On renvoie donc un état à trois branches, pas un booléen :
 *   { possible: true }                    → afficher le bouton
 *   { possible: false, raison: '…' }      → expliquer, sans bouton
 *   null                                  → rien à signaler
 *
 * @param {Array<{value:string}>} options — agents assignables par l'utilisateur
 */
export function actionRetourALaRegle(run, regle, options) {
  if (!estException(run, regle)) return null

  const attendu = normEmail(regle?.provider_email)
  if (run?.status === 'completed') {
    return {
      possible: false,
      cible: attendu,
      raison: 'Ménage terminé — il est dû à l’agent qui l’a fait.',
    }
  }

  const assignables = (Array.isArray(options) ? options : [])
    .map((o) => normEmail(o?.value))
    .filter(Boolean)
  if (!assignables.includes(attendu)) {
    return {
      possible: false,
      cible: attendu,
      raison: 'L’agent de la règle n’est pas dans votre périmètre.',
    }
  }

  return { possible: true, cible: attendu }
}
